import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/d9vc5u6859p3u981do40";

interface FormApiResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

type FormStatus = { type: "success" | "error"; message: string } | null;

const CONTACT_CARDS = [
  {
    icon: "ri-mail-send-line",
    title: "官方邮箱",
    value: "service@89890manhua.com",
    note: "合作洽谈、版权相关请发邮件",
  },
  {
    icon: "ri-customer-service-2-line",
    title: "在线客服",
    value: "每日 9:00 - 23:00",
    note: "阅读问题、账号问题随时咨询",
  },
  {
    icon: "ri-map-pin-line",
    title: "办公地址",
    value: "上海市静安区南京西路898号",
    note: "欢迎合作方预约到访",
  },
];

export default function ContactSection() {
  const [status, setStatus] = useState<FormStatus>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get("website_alt") as string)?.trim() || "";
    if (honeypotValue) {
      setStatus({ type: "success", message: "感谢您的留言，我们会尽快与您联系！" });
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, value as string);
    });

    setSubmitting(true);
    setStatus(null);
    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormApiResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormApiResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      if (response.ok && parsed?.code === "OK") {
        setStatus({ type: "success", message: serverMsg || "感谢您的留言，我们会尽快与您联系！" });
        form.reset();
      } else {
        setStatus({
          type: "error",
          message: serverMsg || "提交失败，请检查填写内容后重试。",
        });
      }
    } catch {
      setStatus({ type: "error", message: "网络异常，提交失败，请稍后重试。" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="container-site py-12 md:py-16" aria-labelledby="contact-title">
      <div className="flex flex-col items-center text-center">
        <span className="chip-tag">联系我们</span>
        <h2 id="contact-title" className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          与我们取得联系
        </h2>
        <p className="mt-2 max-w-xl text-sm text-foreground-600">
          无论是阅读问题、作品推荐还是商务合作，欢迎随时留言，我们会尽快回复您。
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3 md:gap-5">
        {CONTACT_CARDS.map((card) => (
          <div
            key={card.title}
            className="flex flex-col items-start rounded-lg border border-background-200/70 bg-background-100/60 p-5"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-lg text-secondary-800">
              <i className={card.icon} aria-hidden="true" />
            </span>
            <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{card.title}</h3>
            <p className="mt-1 text-sm font-medium text-primary-600">{card.value}</p>
            <p className="mt-1 text-xs text-foreground-600">{card.note}</p>
          </div>
        ))}
      </div>

      <div className="mx-auto mt-8 max-w-3xl rounded-lg border border-background-200/70 bg-background-100/60 p-6 md:p-8">
        <form id="contactForm" data-readdy-form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                您的称呼 <span className="text-primary-500">*</span>
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                maxLength={30}
                placeholder="请输入您的称呼"
                className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                电子邮箱 <span className="text-primary-500">*</span>
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                maxLength={60}
                placeholder="用于接收我们的回复"
                className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
              />
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="contact-subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
              留言主题 <span className="text-primary-500">*</span>
            </label>
            <select
              id="contact-subject"
              name="subject"
              required
              defaultValue=""
              className="w-full rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
            >
              <option value="" disabled>
                请选择留言主题
              </option>
              <option value="阅读问题咨询">阅读问题咨询</option>
              <option value="作品推荐与投稿">作品推荐与投稿</option>
              <option value="合作洽谈">合作洽谈</option>
              <option value="建议与反馈">建议与反馈</option>
              <option value="其他">其它</option>
            </select>
          </div>

          <div className="mt-4">
            <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
              留言内容 <span className="text-primary-500">*</span>
            </label>
            <textarea
              id="contact-message"
              name="message"
              required
              maxLength={500}
              rows={5}
              placeholder="请详细描述您的问题或想法（最多500字）"
              className="w-full resize-none rounded-md border border-background-300/70 bg-background-50 px-4 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
            />
            <p className="mt-1 text-right text-xs text-foreground-500">最多 500 字</p>
          </div>

          {/* 反垃圾字段（对用户隐藏） */}
          <div className="frm-honey" aria-hidden="true">
            <label htmlFor="website-alt">请勿填写此字段</label>
            <input
              id="website-alt"
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              readOnly
              value=""
            />
          </div>

          {status && (
            <div
              role="status"
              className={`mt-4 flex items-start gap-2 rounded-md px-4 py-3 text-sm ${
                status.type === "success"
                  ? "bg-accent-50 text-accent-800 border border-accent-200"
                  : "bg-primary-50 text-primary-800 border border-primary-200"
              }`}
            >
              <i
                className={status.type === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"}
                aria-hidden="true"
              />
              <span>{status.message}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary mt-5 w-full !py-3 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:px-10"
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin text-lg" aria-hidden="true" />
                提交中…
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill text-lg" aria-hidden="true" />
                提交留言
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}